/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext,
    IMarketSettings
} from '@msdyn365-commerce/core';
import {
    OrgUnitsDataActions,
    OrgUnit,
    SearchStoreCriteria,
    IQueryResultSettings,
    ChannelDeliveryOption
} from '@msdyn365-commerce/retail-proxy';

import { IDistributorInfo, QueryResultSettingsProxy } from './utilities';
/**
 * Get distributor variant action input class.
 */
export class GetDistributorListInput implements IActionInput {
    public ChannelId?: number;

    public StoreNumber?: string;

    public InventoryLocationId?: string;

    public queryResultSettings: IQueryResultSettings | undefined;

    constructor(queryResultSettings?: IQueryResultSettings, _ChannelId?: number, _StoreNumber?: string, _InventoryLocationId?: string) {
        this.ChannelId = _ChannelId;
        this.StoreNumber = _StoreNumber;
        this.InventoryLocationId = _InventoryLocationId;
        this.queryResultSettings = queryResultSettings;
    }

    /**
     * Get Cache Key.
     * @returns - Cache key string.
     */
    public getCacheKey = (): string => 'GetDistributorListCache';

    /**
     * Get Cache type Name.
     * @returns - Cache name string.
     */
    public getCacheObjectType = (): string => 'GetDistributorListInput';

    /**
     * Get Cache type.
     * @returns - CacheType enum.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the getDistributor data action.
 * @param inputData - Action context info.
 * @returns GetDistributorInput - The action input.
 */
export const createGetDistributorListInput = (inputData: ICreateActionContext): GetDistributorListInput => {
    if (
        (inputData.requestContext && inputData.requestContext.user && inputData.requestContext.user.isSignedIn) ||
        inputData.requestContext.params.isEditor
    ) {
        const queryResultSettings = QueryResultSettingsProxy.fromInputData(inputData).QueryResultSettings;
        return new GetDistributorListInput(queryResultSettings);
    }

    throw new Error('Unable to create get distributor list. User is not authenticated.');
};

/**
 * Action method for the getDistributor data action.
 * @param input - The action input class.
 * @param ctx - The action context.
 * @returns OrgUnit - The full org unit availability.
 */
export async function getDistributorListAction(input: GetDistributorListInput, ctx: IActionContext): Promise<IDistributorInfo[]> {
    const searchStoreCriteria: SearchStoreCriteria = {
        // Online
        ChannelTypeValue: 3,
        // B2B
        OnlineStoreCustomerTypeValue: 1,
        ChannelId: input.ChannelId,
        StoreNumber: input.StoreNumber,
        InventoryLocationId: input.InventoryLocationId
    };

    const marketSettings = ctx.requestContext.markets;

    return OrgUnitsDataActions.searchAsync(
        {
            callerContext: ctx,
            bypassCache: 'get'
        },
        searchStoreCriteria
    )
        .then(async (channels: OrgUnit[]) => {
            const filteredChannels = filterChannelsBasedUponMarketSettings(channels, marketSettings);
            const channelIdList: number[] = [];
            filteredChannels?.forEach(channel => {
                if (channel !== undefined) {
                    channelIdList.push(channel.RecordId);
                }
            });
            const locationDeliveryOptions = await _getLocationPickUpDeliveryModes(channelIdList, ctx);

            const channelPromiseList = filteredChannels.map(async channel => {
                const channelMarketSettings = getMarketSettingsForChannel(marketSettings, channel.RecordId);
                const channelDeliveryOption = locationDeliveryOptions?.find(
                    channelDeliveryOptions => channelDeliveryOptions.ChannelId === channel.RecordId
                );
                return _getDistributorInfo(channel, channelDeliveryOption, channelMarketSettings);
            });
            return Promise.all(channelPromiseList);
        })
        .catch((error: Error) => {
            ctx.trace('[GetDistributor] error getting distributors from searchAsync');
            ctx.trace(error.message);
            ctx.telemetry.error(error.message);
            ctx.telemetry.debug('[GetDistributor] error getting distributors from searchAsync');
            return [];
        });
}

/**
 * Action method that obtains the store information.
 * @param orgUnit The org unit location.
 * @param storeMap A map that contains store information group by the inventory location id.
 * @param channelDeliveryOptions
 * @param ctx The action context.
 */
async function _getDistributorInfo(
    orgUnit: OrgUnit,
    channelDeliveryOptions: ChannelDeliveryOption | undefined,
    marketSettings?: IMarketSettings
): Promise<IDistributorInfo> {
    if (!orgUnit || !orgUnit.OrgUnitNumber) {
        return { OrgUnit: undefined, OrgUnitDeliveryOptions: undefined, MarketSettings: undefined };
    }

    return {
        OrgUnit: orgUnit,
        OrgUnitDeliveryOptions: channelDeliveryOptions,
        MarketSettings: marketSettings
    };
}

function filterChannelsBasedUponMarketSettings(channels: OrgUnit[], marketSettings: IMarketSettings[] | undefined) {
    const intersection = channels.filter(channel =>
        marketSettings?.some(market => market.channelItem?.retailServerChannelId === channel.RecordId)
    );
    return intersection;
}

function getMarketSettingsForChannel(marketSettings: IMarketSettings[] | undefined, channelId: number): IMarketSettings | undefined {
    return marketSettings?.find(market => market?.channelItem?.retailServerChannelId === channelId);
}

/**
 * Action method that obtains the store information.
 * @param channelCollection The org unit channel Id list.
 * @param ctx The action context.
 */
async function _getLocationPickUpDeliveryModes(
    channelCollection: (number | undefined)[],
    ctx: IActionContext
): Promise<ChannelDeliveryOption[] | undefined> {
    if (channelCollection?.length === 0 || channelCollection === undefined) {
        return undefined;
    }

    const channelIdList: number[] = [];
    channelCollection?.forEach(id => {
        if (id !== undefined) {
            channelIdList.push(id);
        }
    });

    // To get all channel pickup delivery mode filterOption should be 4
    return OrgUnitsDataActions.getChannelDeliveryOptionsAsync(
        {
            callerContext: ctx,
            queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx)
        },
        channelIdList,
        4
    )
        .then((channelDeliveryOptions: ChannelDeliveryOption[]) => {
            if (channelDeliveryOptions && !(channelDeliveryOptions instanceof Error)) {
                return channelDeliveryOptions;
            }

            return undefined;
        })
        .catch((error: Error) => {
            ctx.trace('[getDistributorList][_getLocationPickUpDeliveryModes] error getting availability with channel delivery options');
            ctx.trace(error.message);
            ctx.telemetry.exception(error);
            ctx.telemetry.debug(
                '[getDistributorList] [_getLocationPickUpDeliveryModes] error getting availability with channel delivery options'
            );
            return [];
        });
}

/**
 * The complete getDistributor data action.
 */
export const getDistributorDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-distributor',
    action: <IAction<IDistributorInfo[] | undefined>>getDistributorListAction,
    input: createGetDistributorListInput
});

export default getDistributorDataAction;
